import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Eye, EyeOff, Lock } from "lucide-react";
import { useSuperAdminAuth } from "../context/SuperAdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function SALogin() {
  const { login } = useSuperAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate("/superadmin", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6"
         style={{
           background: "radial-gradient(800px 400px at 20% 20%, rgba(34,211,238,0.20), transparent 60%), radial-gradient(600px 400px at 80% 80%, rgba(167,139,250,0.15), transparent 60%), #0B0F14",
           color: "#E5E7EB",
         }}>
      <form onSubmit={submit} data-testid="sa-login-form"
            className="w-full max-w-md p-8 rounded-3xl border border-white/10 backdrop-blur-xl"
            style={{ background: "rgba(9,13,18,0.6)", boxShadow: "0 30px 80px rgba(0,0,0,0.4)" }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, #22D3EE, #A78BFA)" }}>
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-display text-xl text-white">Super Admin</div>
            <div className="text-[11px] uppercase tracking-widest text-cyan-300/80">Platform Cockpit</div>
          </div>
        </div>
        <p className="text-sm text-white/60 mb-6">
          This is a restricted portal. Client accounts cannot sign in here.
        </p>
        <div className="space-y-4">
          <div>
            <Label htmlFor="sa-email" className="text-xs text-white/60">Email</Label>
            <Input
              id="sa-email"
              data-testid="sa-login-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sa@platform.local"
              className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              required
            />
          </div>
          <div>
            <Label htmlFor="sa-pw" className="text-xs text-white/60">Password</Label>
            <div className="relative mt-1.5">
              <Input
                id="sa-pw"
                data-testid="sa-login-password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white pr-10"
                required
              />
              <button type="button" onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-white/50 hover:text-white">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button
            data-testid="sa-login-submit"
            type="submit"
            disabled={busy || !email || !password}
            className="w-full h-11 text-white font-medium"
            style={{ background: "linear-gradient(135deg, #22D3EE, #A78BFA)" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" />Sign in</>}
          </Button>
        </div>
        <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between text-[11px]">
          <span className="text-white/40">Not a super admin?</span>
          <a href="/" className="text-cyan-300 hover:text-cyan-200 transition" data-testid="sa-login-goto-client">
            Client portal →
          </a>
        </div>
      </form>
    </div>
  );
}
